import type { BiomassDataRow } from './productionUnitTable'
import type { FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { computeCurrentBiomassPerLot, type CurrentBiomassPerLotEntry } from './currentBiomassPerLot'

export type BiomassAsOfResolver = (args: {
  lotId?: string | null
  lotToken: string
  toRecordedAt?: string | null
}) => CurrentBiomassPerLotEntry | null

export const buildBiomassAsOfResolver = (args: {
  productionRows: readonly BiomassDataRow[]
  mortalityLogs: readonly MortalityLog[]
  weightSampleLogs: readonly WeightSampleLog[]
  fishStockLogs: readonly FishStockLog[]
  canonicalLotIdByToken: ReadonlyMap<string, string>
  /**
   * Fase 7 (docs/tier-a-merge-consumer-mapping.md §2.6 line 363): optional
   * parallel map carrying the lineage weight ([0, 1], first-weight-found
   * per Option A) associated with each `lotToken` resolved via
   * `canonicalLotIdByToken`. Kept as a separate map (rather than widening
   * `canonicalLotIdByToken`'s value type) so existing callers that only
   * pass a `Map<string, string>` keep typechecking unchanged. Applied only
   * on the token-fallback path — an explicit `lotId` is an exact match and
   * is never scaled. Defaults to weight 1 (no-op) for any token absent
   * from the map, matching the pre-Fase-7 behavior.
   */
  canonicalWeightByToken?: ReadonlyMap<string, number>
}): BiomassAsOfResolver => {
  const cacheByDate = new Map<string, Map<string, CurrentBiomassPerLotEntry>>()

  const resolveCurrentByLotId = (toRecordedAt?: string): Map<string, CurrentBiomassPerLotEntry> => {
    const cacheKey = toRecordedAt?.trim() || '__latest__'
    const cached = cacheByDate.get(cacheKey)
    if (cached) return cached

    const derived = computeCurrentBiomassPerLot({
      productionRows: args.productionRows,
      mortalityLogs: args.mortalityLogs,
      weightSampleLogs: args.weightSampleLogs,
      fishStockLogs: args.fishStockLogs,
      toRecordedAt: toRecordedAt?.trim() || undefined
    })
    cacheByDate.set(cacheKey, derived)
    return derived
  }

  return ({ lotId, lotToken, toRecordedAt }) => {
    const explicitLotId = typeof lotId === 'string' ? lotId.trim() : ''
    const usedFallback = !explicitLotId
    const fallbackLotId = args.canonicalLotIdByToken.get(lotToken)?.trim() || ''
    const resolvedLotId = explicitLotId || fallbackLotId
    if (!resolvedLotId) return null

    const entry = resolveCurrentByLotId(toRecordedAt?.trim() || undefined).get(resolvedLotId) ?? null
    if (!entry) return null

    // Fase 7 (§2.6 line 363): only the token-fallback path carries a
    // lineage weight; an explicit lotId is an exact match (weight 1).
    // `biomassKg` is the only field scaled — `sourceWeightG` /
    // `sourceStockCount` are count-based fields deferred to Phase 8
    // (fork pattern #9) and stay untouched.
    if (!usedFallback || entry.biomassKg === null) return entry
    const weight = args.canonicalWeightByToken?.get(lotToken) ?? 1
    if (weight === 1) return entry
    return { ...entry, biomassKg: entry.biomassKg * weight }
  }
}
